import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ChevronDownIcon, ChevronRightIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { getSearchHistory, getSearchResults } from '../../Requests/ArticleBibliographyRequests'
import { getTeamUuid } from '../../utils/cookies'
import { sanitizeUserInput } from '../../utils/security'
import SearchResultsList from './SearchResultsList'
import ParticleLoader from "../ParticleLoader";

function SearchHistory({ onArticleSaved }) {
    const { t } = useTranslation()
    const { project_uuid } = useParams()
    const [searchHistory, setSearchHistory] = useState([])
    const [historyLoading, setHistoryLoading] = useState(true)
    const [expandedSearches, setExpandedSearches] = useState(new Set())
    const [searchResults, setSearchResults] = useState({}) // Cache results by search ID
    const [loadingResults, setLoadingResults] = useState(new Set())

    useEffect(() => {
        loadSearchHistory()
    }, [])

    const loadSearchHistory = async () => {
        try {
            setHistoryLoading(true)
            const response = await getSearchHistory(getTeamUuid(), 'completed')
            // Sort by most recent first
            const sortedHistory = response.data.data.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
            )
            setSearchHistory(sortedHistory)
        } catch (error) {
            console.error('Error loading search history:', error)
            setSearchHistory([])
        } finally {
            setHistoryLoading(false)
        }
    }

    const loadSearchResults = async (searchId) => {
        if (searchResults[searchId] || loadingResults.has(searchId)) {
            return // Already loaded or loading
        }

        try {
            setLoadingResults(prev => new Set([...prev, searchId]))
            const response = await getSearchResults(searchId)
            setSearchResults(prev => ({
                ...prev,
                [searchId]: response.data
            }))
        } catch (error) {
            console.error('Error loading search results:', error)
            setSearchResults(prev => ({
                ...prev,
                [searchId]: []
            }))
        } finally {
            setLoadingResults(prev => {
                const newSet = new Set(prev)
                newSet.delete(searchId)
                return newSet
            })
        }
    }

    const toggleSearchExpanded = (searchId) => {
        const newExpanded = new Set(expandedSearches)
        if (expandedSearches.has(searchId)) {
            newExpanded.delete(searchId)
        } else {
            newExpanded.add(searchId)
            loadSearchResults(searchId) // Load results when expanding
        }
        setExpandedSearches(newExpanded)
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />
            case 'failed':
                return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            case 'processing':
                return <ArrowPathIcon className="h-5 w-5 text-amber-400 animate-spin" />
            default:
                return <ClockIcon className="h-5 w-5 text-gray-400" />
        }
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffInHours = (now - date) / (1000 * 60 * 60)

        if (diffInHours < 1) {
            return t('common.time.justNow')
        } else if (diffInHours < 24) {
            return t('common.time.hoursAgo', { hours: Math.floor(diffInHours) })
        } else if (diffInHours < 48) {
            return t('common.time.yesterday')
        } else {
            return date.toLocaleDateString()
        }
    }

    if (historyLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <ParticleLoader size={24} />
                <span className="ml-2 text-gray-600">{t('common.loading')}</span>
            </div>
        )
    }

    if (searchHistory.length === 0) {
        return (
            <div className="text-center py-8">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    {t('articleBibliography.history.empty')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                    {t('articleBibliography.history.emptyDescription')}
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
                <ClockIcon className="h-5 w-5 text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900">
                    {t('articleBibliography.history.title')}
                </h3>
                <span className="text-sm text-gray-500">
                    ({searchHistory.length} {t('articleBibliography.history.searches')})
                </span>
            </div>

            <div className="space-y-3">
                {searchHistory.map((search) => (
                    <div key={search.id} className="border border-gray-200 rounded-lg">
                        <div
                            className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleSearchExpanded(search.id)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    {expandedSearches.has(search.id) ? (
                                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                                    )}
                                    
                                    {getStatusIcon(search.status)}
                                    
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2">
                                            <h4 className="text-sm font-medium text-gray-900 truncate">
                                                {sanitizeUserInput(search.search_query.replace(/^"|"$/g, ''))}
                                            </h4>
                                            {search.total_results !== null && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    {search.total_results.toLocaleString()} {t('articleBibliography.history.results')}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {formatDate(search.created_at)}
                                            {search.search_params?.filters?.publication_year && (
                                                <span className="ml-2">
                                                    • {t('articleBibliography.search.filters.publicationYear')}: {search.search_params.filters.publication_year}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {expandedSearches.has(search.id) && (
                            <div className="border-t border-gray-200 px-4 py-4 bg-gray-50">
                                {loadingResults.has(search.id) ? (
                                    <div className="flex items-center justify-center py-6">
                                        <ParticleLoader size={20} />
                                        <span className="ml-2 text-sm text-gray-600">
                                            {t('articleBibliography.history.loadingResults')}
                                        </span>
                                    </div>
                                ) : (
                                    <SearchResultsList
                                        currentSearch={search}
                                        results={searchResults[search.id] || []}
                                        projectId={project_uuid}
                                        onArticleSaved={onArticleSaved}
                                        loading={false}
                                        isHistoryView={true}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default SearchHistory